import { useState } from 'react';
import { load, save } from './storage';
import type { PlaceGroup, PlaceKind, PlaceSummary, TripKind } from './types';

const KIND_LABELS: Record<PlaceKind, string> = {
  Bar: 'Bar',
  Restaurant: 'Restaurant',
  Diner: 'Diner',
  Bbq: 'BBQ',
  Cafe: 'Cafe',
  DriveIn: 'Drive-in',
  Bakery: 'Bakery',
  IceCream: 'Ice cream',
  SupperClub: 'Supper club',
};

/** "Diner", "BBQ", "Drive-in"... Unknown kinds (a newer API) read as a plain "Place". */
export function kindLabel(kind: PlaceKind | null | undefined): string {
  return (kind && KIND_LABELS[kind]) ?? 'Place';
}

/** A bar, as opposed to a bite. Places from before kinds existed were all bars. */
export function isBar(kind: PlaceKind | null | undefined): boolean {
  return !kind || kind === 'Bar';
}

/** Whether a place shows under the Bars / Bites / Both choice. */
export function inGroup(kind: PlaceKind | null | undefined, group: PlaceGroup): boolean {
  return group === 'all' || (group === 'bars') === isBar(kind);
}

export const GROUPS: { value: PlaceGroup; label: string }[] = [
  { value: 'bars', label: 'Bars' },
  { value: 'bites', label: 'Bites' },
  { value: 'all', label: 'Both' },
];

const GROUP_KEY = 'bbh.group.v1';

/** The Bars / Bites / Both choice, remembered on this device across the map, home and planner. */
export function useGroup(): [PlaceGroup, (g: PlaceGroup) => void] {
  const [group, setGroup] = useState<PlaceGroup>(() => {
    const g = load<string>(GROUP_KEY, 'all');
    return g === 'bars' || g === 'bites' ? g : 'all';
  });
  return [
    group,
    (g) => {
      save(GROUP_KEY, g);
      setGroup(g);
    },
  ];
}

export const TRIP_KINDS: { value: TripKind; label: string; hint: string }[] = [
  { value: 'RoadTrip', label: 'Road trip', hint: 'A to B, with stops along the way' },
  { value: 'DayOut', label: 'Day out', hint: 'One good spot, or a few around town' },
  { value: 'BarHop', label: 'Bar hop', hint: 'A crawl with a named, sober driver' },
];

export function tripKindLabel(kind: TripKind): string {
  return TRIP_KINDS.find((k) => k.value === kind)?.label ?? 'Trip';
}

/** Around town and curated routes: all bars makes it a bar hop, anything else a day out. */
export function kindForStops(stops: Pick<PlaceSummary, 'kind'>[]): TripKind {
  return stops.length > 0 && stops.every((s) => isBar(s.kind)) ? 'BarHop' : 'DayOut';
}

/** Shown on a bar stop when the trip isn't a bar hop (no designated driver was named). */
export const SODA_NOTE = "Whoever's driving the next leg, stick to soda.";
